// packages/ui/src/forms/_i18n/FormI18nContext.tsx
'use client';

import * as React from 'react';
import type { TFunc } from '@tourtask/types/i18n';
import type { CommonKey } from '@tourtask/i18n/keys';

/**
 * A translator whose KEY SPACE this package does not know.
 *
 * `<Form>` is used from the backoffice (a `common` translator), the marketing site (a
 * `public` one) and embed (`getStr`). A function is contravariant in its parameter, so
 * none of those is assignable to another's key union — and `TFunc` (i.e. `TFunc<CommonKey>`)
 * would reject the public one at the prop.
 *
 * The CONTEXT stores it at `common`, because `<Form>` is generic in its key union and
 * React context is not: a context generic in the key would have to be created per
 * namespace. `<Form>` erases the difference on the way in, which is sound here for the one
 * thing the context is used for — resolving a react-hook-form `message` that carries a
 * catalog code, through `renderFieldError`, which probes membership before translating.
 */
export type AnyNamespaceTranslator = TFunc<CommonKey> & { has?(key: string): boolean };

type Ctx = {
  t?: AnyNamespaceTranslator;
};

const FormI18nContext = React.createContext<Ctx>({});

export function FormI18nProvider({
  t,
  children,
}: {
  t?: AnyNamespaceTranslator;
  children: React.ReactNode;
}) {
  return <FormI18nContext.Provider value={{ t }}>{children}</FormI18nContext.Provider>;
}

export function useFormT(): AnyNamespaceTranslator | undefined {
  return React.useContext(FormI18nContext).t;
}
